<script setup>
import { ref, getCurrentInstance } from 'vue';
import { router } from '@inertiajs/vue3';
import { useToast } from 'primevue/usetoast';

const props = defineProps({
    visible: Boolean,
    item: Object,
});

const emit = defineEmits(['update:visible', 'reject']);

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();

const approvalProcessing = ref(false);
const approvalMessage = ref('');

const close = () => {
    approvalMessage.value = '';
    emit('update:visible', false);
};

const approve = () => {
    approvalProcessing.value = true;
    router.post(
        proxy.route('client.order-content.client-approve', props.item.content_id),
        { message: approvalMessage.value.trim() || null },
        {
            onSuccess: () => {
                toast.add({ severity: 'success', summary: $t('Success'), detail: $t('Content approved successfully'), life: 3000 });
                close();
            },
            onError: (errors) => {
                const msg = errors?.error || errors?.message || $t('Failed to approve content');
                toast.add({ severity: 'error', summary: $t('Error'), detail: msg, life: 5000 });
            },
            onFinish: () => { approvalProcessing.value = false; }
        }
    );
};

const rejectFromModal = () => {
    close();
    emit('reject', props.item.content_id);
};
</script>

<template>
    <Dialog
        :visible="visible"
        @update:visible="(v) => emit('update:visible', v)"
        :header="$t('Review Article')"
        modal
        :closable="!approvalProcessing"
        :style="{ width: '55vw' }"
        :breakpoints="{ '960px': '80vw', '640px': '95vw' }"
        @hide="close"
    >
        <div v-if="item" class="space-y-5">
            <!-- Info banner -->
            <div class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div class="flex items-start gap-3">
                    <i class="pi pi-info-circle text-blue-600 text-lg mt-0.5"></i>
                    <div>
                        <p class="font-medium text-blue-800 mb-1">{{ $t('Article ready for your review') }}</p>
                        <p class="text-sm text-blue-600">{{ $t('Please read the article carefully before approving or rejecting it.') }}</p>
                    </div>
                </div>
            </div>

            <!-- Site info -->
            <div class="flex items-center gap-3 p-3 bg-surface-50 rounded-lg border border-surface-200">
                <img :src="`https://www.google.com/s2/favicons?domain=${item.url}`" class="w-5 h-5" />
                <div>
                    <p class="text-xs text-surface-500">{{ $t('Portal') }}</p>
                    <p class="font-semibold text-surface-900">{{ item.url }}</p>
                </div>
            </div>

            <!-- Google Docs link -->
            <div v-if="item.content_link" class="p-4 border-2 border-blue-300 rounded-xl bg-white">
                <p class="text-sm font-semibold text-surface-700 mb-3">{{ $t('Article Link (Google Docs)') }}</p>
                <a
                    :href="item.content_link"
                    target="_blank"
                    class="flex items-center gap-3 p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors group"
                >
                    <div class="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center shrink-0">
                        <i class="pi pi-file-edit text-white text-lg"></i>
                    </div>
                    <div class="flex-1 min-w-0">
                        <p class="font-medium text-blue-800 group-hover:underline truncate">{{ item.content_link }}</p>
                        <p class="text-xs text-blue-500">{{ $t('Click to open in Google Docs') }}</p>
                    </div>
                    <i class="pi pi-external-link text-blue-500 shrink-0"></i>
                </a>
            </div>
            <div v-else class="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div class="flex items-center gap-2 text-amber-700">
                    <i class="pi pi-exclamation-triangle"></i>
                    <span class="text-sm">{{ $t('No article link available yet') }}</span>
                </div>
            </div>

        </div>

        <template #footer>
            <div class="flex flex-col w-full gap-3">
                <Textarea
                    v-model="approvalMessage"
                    rows="2"
                    class="w-full"
                    :placeholder="$t('Leave a message for the admin (optional)...')"
                    :disabled="approvalProcessing"
                />
                <div class="flex justify-end gap-2">
                    <Button
                        :label="$t('Reject')"
                        icon="pi pi-times"
                        severity="danger"
                        outlined
                        :disabled="approvalProcessing || !item?.content_link"
                        @click="rejectFromModal"
                    />
                    <Button
                        :label="$t('Approve')"
                        icon="pi pi-check"
                        severity="success"
                        :loading="approvalProcessing"
                        :disabled="approvalProcessing || !item?.content_link"
                        @click="approve"
                    />
                </div>
            </div>
        </template>
    </Dialog>
</template>
